import { formatPesoAmount } from './money';
export const toInventoryNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatInventoryQuantity = (value, decimals = 3) => {
  const numeric = toInventoryNumber(value);
  return numeric.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const toMetricUnitKey = (value) => String(value || '').trim().toLowerCase();

export const usesUnitInventory = (row = {}) =>
  Boolean(row?.usesUnitInventory || row?.inventoryMode === 'unit' || row?.isResaleOnly || row?.categoryIsResaleOnly);

export const getInventoryDisplayUnit = (row = {}) => {
  if (!usesUnitInventory(row)) return 'kg';
  return row.inventoryMetricUnit
    || row.metricUnit
    || row.stockedUnits?.find((unit) => unit?.metricUnit)?.metricUnit
    || 'unidad';
};

export const formatKg = (value) => `${formatInventoryQuantity(value)} kg`;

export const formatInventoryMeasure = (row = {}, value, decimals = 3) => {
  const unit = getInventoryDisplayUnit(row);
  const label = unit === 'pack' && Math.abs(Number(value)) !== 1 ? 'packs' : unit;
  return `${formatInventoryQuantity(value, decimals)} ${label}`;
};

export const getInventoryFormatLabel = (row = {}) => {
  const parts = String(row.packLabel || row.inventoryMetricUnit || 'Granel').split(' · ');
  return parts.filter((part, index) => !parts.slice(0, index).some(previous => previous === part || previous.endsWith(` ${part}`))).join(' · ');
};

export const formatInventoryCurrency = (value) => `$${formatPesoAmount(toInventoryNumber(value))}`;

export const getInventoryProductImage = (row = {}) =>
  row?.priceImage
  || row?.varietyImage
  || row?.productImage
  || row?.imageUrl
  || row?.Variety?.varietyImage
  || row?.Variety?.Product?.productImage
  || row?.Product?.productImage
  || 'https://placehold.co/96';

export const formatStockedUnits = (units = []) => {
  if (!Array.isArray(units) || units.length === 0) return '-';
  return units
    .filter((unit) => unit && unit.metricUnit)
    .map((unit) => `${formatInventoryQuantity(unit.availableQuantity)} ${unit.metricUnit}`)
    .join(' / ') || '-';
};

export const getInventoryStatusMeta = (row = {}) => {
  if (row.status === 'warning' || Number(row.unresolvedMovementCount || 0) > 0) {
    return {
      label: 'Revisar',
      className: 'bg-amber-100 text-amber-800 border-amber-200',
    };
  }
  if (row.status === 'negative' || toInventoryNumber(row.availableQuantity ?? row.availableKg) < 0) {
    return {
      label: 'Negativo',
      className: 'bg-red-100 text-red-700 border-red-200',
    };
  }
  return {
    label: 'OK',
    className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
};

export const resolveInventoryUnitFactor = (row = {}, metricUnit) => {
  const unitKey = toMetricUnitKey(metricUnit);
  const inventoryUnitKey = toMetricUnitKey(
    row.inventoryMetricUnitKey
    || row.inventoryMetricUnit
    || row.inventoryBaseUnit
    || row.baseUnit
    || row.metricUnit
    || 'kg'
  );
  if (!unitKey || unitKey === inventoryUnitKey) return 1;
  if (inventoryUnitKey === 'kg' && (unitKey === 'kg' || unitKey.includes('kg') || unitKey.includes('kilo'))) return 1;
  const option = (row?.unitOptions || []).find((entry) =>
    String(entry.metricUnit || '').trim().toLowerCase() === unitKey
    || String(entry.metricUnitKey || '').trim().toLowerCase() === unitKey
  );
  if (option?.useScaleWeight) return 1;
  const effectiveBaseQuantity = Number(option?.effectiveBaseQuantityPerUnit);
  if (Number.isFinite(effectiveBaseQuantity) && effectiveBaseQuantity > 0) return effectiveBaseQuantity;
  const baseQuantity = Number(option?.baseQuantityPerUnit);
  if (Number.isFinite(baseQuantity) && baseQuantity > 0) return baseQuantity;
  const explicit = Number(option?.unitWeightKg);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const proportional = Number(option?.kgPerUnit);
  if (Number.isFinite(proportional) && proportional > 0) return proportional;
  if (
    inventoryUnitKey === 'unidad'
    && (row.categoryIsResaleOnly || row.isResaleOnly)
  ) {
    return 1;
  }
  return null;
};

export const getInventoryKgPreview = (row = {}, quantity, metricUnit) => {
  const parsedQuantity = Number(quantity);
  if (!Number.isFinite(parsedQuantity) || parsedQuantity < 0) return null;
  if (parsedQuantity === 0) return 0;
  const factor = resolveInventoryUnitFactor(row, metricUnit);
  return factor ? parsedQuantity * factor : null;
};

export const getInventoryVariancePreview = (row = {}, quantity, metricUnit) => {
  const kgPreview = getInventoryKgPreview(row, quantity, metricUnit);
  if (kgPreview === null) return null;
  return kgPreview - toInventoryNumber(row.availableQuantity ?? row.availableKg);
};

export const getCountSessionStatusMeta = (session = {}) => {
  if (session.status === 'completed') {
    return {
      label: 'Finalizado',
      className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
  }
  return {
    label: 'Borrador',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
  };
};

export const buildInventoryWarningText = (warnings = []) => {
  const safeWarnings = Array.isArray(warnings) ? warnings : [];
  if (!safeWarnings.length) return '';
  return safeWarnings
    .map((warning) => warning?.message || '')
    .filter(Boolean)
    .join('\n');
};
